import { useCallback, useContext } from "react"
import { useTranslation } from "react-i18next"
import { XmppContext } from "../stream/xmppClient"
import { useAppSelector } from "../reducers/hooks"
import {
  selectActiveChat,
  selectMessageChat,
} from "../reducers/chatsSliceSelectors"
import AdHocCommands from "../api/adHocCommands"
import FileShareResultParser from "../lib/fileShareResultParser"
import ClipboardWriter from "../lib/clipboardWriter"
import { Message } from "../reducers/messagesSlice"
import { format } from "date-fns"
import { toast } from "sonner"

const useHandleShareLink = (message: Message) => {
  const { t } = useTranslation()
  const { myJid, client } = useContext(XmppContext)
  const activeChat = useAppSelector(selectActiveChat)
  const messageChat = useAppSelector(
    selectMessageChat(message, myJid.toString()),
  )

  return useCallback(
    (filename: string) =>
      ClipboardWriter.executeAndWriteResult(async () => {
        window.analytics.track("ShareFileLinkGenerated", {
          userJid: myJid.toString(),
          messageId: message.id,
          fileUrl: message.url,
          chatJid: activeChat.jid,
        })

        if (message.url) {
          const response = await AdHocCommands.shareFile({
            client,
            fileUrl: message.url,
            messageId: message.id,
            messageRoomJid: messageChat?.jid,
            triggeringRoomJid: activeChat.jid,
            userJid: myJid.toString(),
          })

          const fileShareResult = FileShareResultParser.parse(response)
          if (fileShareResult?.shareToken) {
            toast.success(t("planOrder.shareFile.linkCopied"))
          } else {
            toast.error(t("planOrder.shareFile.failed"))
          }

          const formattedDate = format(new Date(), "yyyy-MM-dd_HH-mm")
          const extension = filename.split(".").pop()

          filename = `${formattedDate}.${extension}`

          return `${import.meta.env.REACT_APP_FILES_ENDPOINT}/${fileShareResult?.shareToken}/${filename}`
        }
      }),
    [message, client, myJid, activeChat, messageChat, t],
  )
}

export default useHandleShareLink
